import { canAccessGmailIntegration } from "../../utils/gmailScope";
import { canAccessSecurityConsole } from "../../utils/securityScope";

/** Paths that must never be auth:true for non-admin roles (even if saved in Dynamo). */
export const ADMIN_ONLY_ROUTE_PATHS = [
  "/konfigurimet/sistemi/rotacioni",
  "/konfigurimet/kompania/timezone/ekipeve",
];

const ADMIN_ROLES = new Set(["admin", "super admin", "administrator"]);

/** Routes that must stay reachable even when an older saved routeConfig omits them. */
export const REQUIRED_ROUTES = [
  {
    exact: false,
    path: "/problematikat",
    title: "Support",
    auth: true,
    write: true,
    // All roles — employees submit own tickets; admins resolve.
  },
  {
    exact: false,
    path: "/pushimet/planer",
    title: "Planifikuesi i pushimeve",
    auth: true,
    // Employees get route access; write actions are gated in PlanerPage UI.
    write: true,
    roles: [
      "admin",
      "super admin",
      "supervisor",
      "branch manager",
      "employee",
    ],
  },
  {
    exact: false,
    path: "/konfigurimet/integrime/gmail",
    title: "Integrime: Gmail Inbox",
    auth: true,
    write: true,
    // Email allowlist only — not role-based.
    gmailAllowlistOnly: true,
  },
  {
    exact: false,
    path: "/konfigurimet/siguria/sesionet",
    title: "Siguria: Menaxheri i sesioneve",
    auth: true,
    write: true,
    // Email allowlist only — Super Admin role is not enough.
    securityAllowlistOnly: true,
  },
  {
    exact: false,
    path: "/konfigurimet/kompania/timezone/ekipeve",
    title: "Timezone e ekipeve",
    auth: true,
    write: true,
    roles: ["admin", "super admin"],
  },
  {
    exact: false,
    path: "/konfigurimet/sistemi/rotacioni",
    title: "Sistemi: Rotacioni i turneve",
    auth: true,
    write: true,
    roles: ["admin", "super admin"],
  },
  {
    exact: false,
    path: "/analitika",
    title: "Analitika",
    auth: true,
    write: true,
    roles: ["admin", "super admin", "branch manager"],
  },
];

export function normalizeRole(role) {
  return String(role || "")
    .toLowerCase()
    .trim();
}

export function isAdminRole(userRole) {
  return ADMIN_ROLES.has(normalizeRole(userRole));
}

/**
 * Pick the most specific routeConfig entry for a pathname.
 * Avoids `/konfigurimet` (auth:true) winning over `/konfigurimet/sistemi/rotacioni` (auth:false).
 */
export function findBestRouteMatch(configs, pathname, matchPathFn) {
  const list = Array.isArray(configs) ? configs : [];
  let best = null;
  let bestLen = -1;
  for (const c of list) {
    if (!c?.path) continue;
    const matched = matchPathFn(
      { path: c.path, end: false },
      pathname
    );
    if (!matched) continue;
    const len = String(c.path).length;
    if (len > bestLen) {
      best = c;
      bestLen = len;
    }
  }
  return best;
}

/**
 * Merge required routes into a user's routeConfig without duplicating paths.
 * @param {Array} routeConfig
 * @param {string} userRole
 * @param {object|string} [authOrEmail] auth context or email — required for Gmail
 */
export function ensureRouteAccess(routeConfig = [], userRole, authOrEmail) {
  let configs = Array.isArray(routeConfig) ? [...routeConfig] : [];
  const role = normalizeRole(userRole);
  const admin = isAdminRole(role);
  const authCtx =
    typeof authOrEmail === "string" ? { email: authOrEmail } : authOrEmail;
  const gmailOk = canAccessGmailIntegration(authCtx);
  const securityOk = canAccessSecurityConsole(authCtx);

  // Strip Gmail for everyone not on the hard allowlist (even if saved config has it).
  if (!gmailOk) {
    configs = configs.filter(
      (c) => !String(c?.path || "").includes("/konfigurimet/integrime/gmail")
    );
  }
  if (!securityOk) {
    configs = configs.filter(
      (c) => !String(c?.path || "").includes("/konfigurimet/siguria/sesionet")
    );
  }

  REQUIRED_ROUTES.forEach((item) => {
    if (item.gmailAllowlistOnly) {
      if (!gmailOk) return;
    } else if (item.securityAllowlistOnly) {
      if (!securityOk) return;
    } else {
      // Role list is authoritative — do NOT infer admin from having /konfigurimet.
      const roleOk = !item.roles?.length || item.roles.includes(role);
      if (!roleOk) return;
    }

    const existingIdx = configs.findIndex((c) => c?.path === item.path);

    const entry = {
      exact: item.exact,
      path: item.path,
      title: item.title,
      auth: item.auth,
      write: item.write,
    };

    if (existingIdx >= 0) {
      configs[existingIdx] = {
        ...configs[existingIdx],
        ...entry,
      };
    } else {
      configs.push(entry);
    }
  });

  // Force-deny admin-only settings even if an old Dynamo accessConfig has auth:true.
  if (!admin) {
    configs = configs.map((c) => {
      const path = String(c?.path || "");
      if (ADMIN_ONLY_ROUTE_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) {
        return { ...c, auth: false };
      }
      return c;
    });
  }

  return configs;
}
